import { FC, useState } from 'react';
import {
  TextBody,
  TextCaption,
  Spacer,
  Stack,
  Card,
  Icon,
  IconButton,
  Textarea,
  Tooltip,
  Input,
  Button,
} from '@pypestream/design-system';
import { useFormContext } from 'react-hook-form';
import '../../modals/create-solution-modal/step-agent.scss';
import {
  useKnowledgeAgentUpload,
  useBobcatFlag,
  useParseSop,
} from '../../../hooks';
import { sendUserMessage } from '../../../xstate/app.xstate-utils';
import {
  KnowledgeSearchNotice,
  KnowledgeSearchSkippedNote,
} from '../../knowledge-search/knowledge-search-notice';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File | null;
  serverId?: string; // ID from server for existing files
}

interface TabAgentProps {
  uploadedFiles: UploadedFile[];
  viewOnly?: boolean;
  addFile: (file: File) => void;
  removeFile: (fileId: string) => void;
}

/**
 * The SOP paste-and-generate front door, and the `knowledgeAgentContext`
 * field it fills. Both gated on the master (`fiveStageNav`):
 * `knowledgeAgentContext` already exists on the SolutionVersion schema and
 * already round-trips (`buildVersionInput`), but until this surface it was
 * only ever editable from the Create Solution wizard's gated intake step
 * (`step-agent.tsx`, also behind the master) — never here, on the
 * panel every existing solution actually uses. See `bobcat-flags.ts`.
 */
const SopFrontDoor: FC<{ viewOnly?: boolean }> = ({ viewOnly }) => {
  const { setValue } = useFormContext();
  const { parseSop, state } = useParseSop();
  const [sopText, setSopText] = useState('');

  const onGenerate = async () => {
    const result = await parseSop(sopText);
    if (!result) {
      sendUserMessage({
        type: 'error',
        text: 'Could not generate settings from that text. Try again, or fill in the fields below directly.',
        autoClose: 4000,
      });
      return;
    }
    // All three fields are set unconditionally, including an empty
    // brandName — Generate is authoritative over whatever was there before,
    // same as tone and knowledgeAgentContext. A conditional skip on brandName
    // alone left it silently stale (old value survived a Generate that
    // legitimately found no brand in the new SOP) while the other two always
    // refreshed — an inconsistent, invisible mix the designer had no way to
    // notice before Save. Reviewing/editing after Generate is the safeguard,
    // not a per-field guess about which value to trust.
    setValue('brandName', result.brandName, { shouldDirty: true });
    setValue('tone', result.tone, { shouldDirty: true });
    setValue('knowledgeAgentContext', result.knowledgeAgentContext, {
      shouldDirty: true,
    });
  };

  return (
    <div>
      <Stack alignItems="center" gutter="2xsmall">
        <TextBody size="small">Paste or describe your SOP</TextBody>
        <Tooltip
          placement="right"
          content="Generates Brand Name, Tone and Global Instructions below from your SOP — review and edit anything before saving."
        >
          <Icon name="info" color="gray" size="xsmall" />
        </Tooltip>
      </Stack>
      <Spacer size="2xsmall" />
      <Textarea
        test-id="ps-agents-sop-textarea"
        variant="outlined"
        placeholder="Paste your SOP, or describe how this agent should behave"
        rows={4}
        value={sopText}
        onChange={(e) => setSopText(e.target.value)}
        disabled={viewOnly || state === 'loading'}
      />
      <Spacer size="2xsmall" />
      <Button
        test-id="ps-agents-sop-generate-btn"
        size="small"
        variant="secondary"
        disabled={viewOnly || state === 'loading' || !sopText.trim()}
        onClick={onGenerate}
      >
        {state === 'loading' ? 'Generating…' : 'Generate'}
      </Button>
    </div>
  );
};

const KnowledgeAgentContextField: FC<{ viewOnly?: boolean }> = ({
  viewOnly,
}) => {
  const { register } = useFormContext();

  return (
    <div>
      <Stack alignItems="center" gutter="2xsmall">
        <TextBody size="small">Global Instructions</TextBody>
        <Tooltip
          placement="right"
          content="Standing instructions for this Knowledge Agent — tone, required disclosures, escalation rules. Applies solution-wide."
        >
          <Icon name="info" color="gray" size="xsmall" />
        </Tooltip>
      </Stack>
      <Spacer size="2xsmall" />
      <Textarea
        test-id="ps-agents-knowledge-agent-context-textarea"
        variant="outlined"
        placeholder="Global Instructions"
        rows={5}
        {...register('knowledgeAgentContext')}
        disabled={viewOnly}
      />
    </div>
  );
};

export const TabAgent: FC<TabAgentProps> = ({
  uploadedFiles,
  viewOnly,
  addFile,
  removeFile,
}) => {
  const { register } = useFormContext();
  const globalInstructionsAccess = useBobcatFlag('fiveStageNav');

  const { customFileUpload, formatFileSize, getFileIcon } =
    useKnowledgeAgentUpload({
      addFile,
      testId: 'ps-agents-ka-file-upload',
      disabled: viewOnly,
    });

  return (
    <>
      {globalInstructionsAccess === 'allowed' && (
        <>
          <SopFrontDoor viewOnly={viewOnly} />
          <Spacer />
        </>
      )}
      <div>
        <Stack alignItems="center" gutter="2xsmall">
          <TextBody size="small">Brand Name</TextBody>
          <Tooltip
            placement="right"
            content="The name of your company or brand. This helps the model respond in a way that aligns with your brand identity."
          >
            <Icon name="info" color="gray" size="xsmall" />
          </Tooltip>
        </Stack>
        <Spacer size="2xsmall" />
        <Input
          test-id="ps-agents-brand-name-input"
          variant="outlined"
          placeholder="Brand Name"
          {...register('brandName')}
          disabled={viewOnly}
        />
      </div>
      <Spacer />
      <div>
        <Stack alignItems="center" gutter="2xsmall">
          <TextBody size="small">Tone</TextBody>
          <Tooltip
            placement="right"
            content="Provide context or instructions for the knowledge agent to understand its role and capabilities."
          >
            <Icon name="info" color="gray" size="xsmall" />
          </Tooltip>
        </Stack>
        <Spacer size="2xsmall" />
        <Textarea
          test-id="ps-agents-tone-textarea"
          variant="outlined"
          placeholder="Tone"
          rows={3}
          {...register('tone')}
          disabled={viewOnly}
        />
      </div>
      {globalInstructionsAccess === 'allowed' && (
        <>
          <Spacer />
          <KnowledgeAgentContextField viewOnly={viewOnly} />
        </>
      )}
      <Spacer />
      <KnowledgeSearchNotice inAgentsPanel viewOnly={viewOnly} />
      <KnowledgeSearchSkippedNote />
      {!viewOnly && customFileUpload}

      {uploadedFiles?.length > 0 && (
        <>
          <Spacer size="xsmall" />
          <div className="c-create-solution-agent__files-list c-create-solution-agent__files-list--panel">
            <Stack gutter="xsmall">
              {uploadedFiles?.map((file) => (
                <Card
                  key={file.id}
                  test-id={`ps-agents-file-card-${file.id}`}
                  size="small"
                  shadow="none"
                  className="c-create-solution-agent__file c-create-solution-agent__file--large"
                >
                  <Icon
                    name={getFileIcon(file.name)}
                    className="c-create-solution-agent__file-icon"
                  />
                  <TextBody>{file.name}</TextBody>
                  <TextCaption>{formatFileSize(file.size)}</TextCaption>
                  {/* <IconButton
                    size="small"
                    name="show"
                    slot="hover-action-start"
                  /> */}
                  <IconButton
                    test-id={`ps-agents-file-delete-btn-${file.id}`}
                    size="small"
                    name="delete"
                    slot="hover-action"
                    onClick={() => removeFile(file.id)}
                  />
                </Card>
              ))}
            </Stack>
          </div>
        </>
      )}
    </>
  );
};
